import { z } from "zod";

import {
  ALMOST_SOLD_OUT_AT,
  FILLING_UP_AT,
} from "../../availability/departure-scarcity";
import { DURATION_UNITS } from "../../duration/duration-bands";
import { moneyDtoSchema } from "../../value-objects/money.schema";
import { pageSchema } from "../pagination";

/**
 * `GET /v1/departures` — upcoming departures across an agency's published
 * tours, for the Departure Dates block (docs/features/departure-dates).
 *
 * ONE ROW PER (tour, date, start time). A tour whose departures fan out per
 * package is folded into one row: the lowest from price, and the seats of the
 * package with the most left (design.json open question 1). The traveler
 * picks the package on the tour page Book now opens. The row is guaranteed
 * to run only when every open package on that date is.
 *
 * TOURS TRAVEL ONCE. Each row names its tour by id; the tour itself — name,
 * slug, length, group size — is in the answer's `tours`, which lists
 * every tour the block covers, whether or not it has a row on this page. The
 * block's tour picker is built from the same list.
 */

/**
 * The style guide's bands, from the same constants the tour page reads
 * (`departure-scarcity.ts`), so a date reads the same on the block and on
 * the tour page Book now opens (AC-02).
 */
export const DEPARTURE_SEAT_STATUSES = [
  "available",
  "filling_up",
  "almost_sold_out",
  "sold_out",
] as const;
export type DepartureSeatStatus = (typeof DEPARTURE_SEAT_STATUSES)[number];

/** `null` seats left is a tour with no seat limit: always available. */
export function departureSeatStatus(
  spotsLeft: number | null,
): DepartureSeatStatus {
  if (spotsLeft === null) return "available";
  if (spotsLeft <= 0) return "sold_out";
  if (spotsLeft <= ALMOST_SOLD_OUT_AT) return "almost_sold_out";
  return spotsLeft <= FILLING_UP_AT ? "filling_up" : "available";
}

export const DEPARTURE_SORTS = ["soon", "price", "seats"] as const;
export type DepartureSort = (typeof DEPARTURE_SORTS)[number];
export const DEFAULT_DEPARTURE_SORT: DepartureSort = "soon";

export const DEPARTURES_PAGE_SIZE = 10;
export const MAX_DEPARTURES_PER_PAGE = 50;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const csv = z.string().transform((v) =>
  v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
);

/**
 * The query. Public, so `.strip()`: an unknown parameter is ignored, never
 * refused (AC-15). `scope` is the block's hand-picked tours, by id, so a
 * renamed tour stays picked; `tour` is the visitor's filter, by slug, as the
 * page address carries it. The server answers with their intersection, so an
 * address can never widen a hand-picked block (AC-08). An absent `year` is
 * the first year that has departures, and the answer says which (`year`);
 * `allYears` lists every year at once, and the answer's `year` is `null`.
 */
export const departuresQuerySchema = z
  .object({
    scope: csv.optional(),
    tour: csv.optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
    sort: z.enum(DEPARTURE_SORTS).default(DEFAULT_DEPARTURE_SORT),
    hideSoldOut: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .default(false),
    /** Every year at once, ignoring `year` and `month`: a block whose
     *  filters are hidden lists the next departures, whatever the year. */
    allYears: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .default(false),
    max: z.coerce.number().int().min(1).optional(),
    page: pageSchema.default(1),
    perPage: z.coerce
      .number()
      .int()
      .min(1)
      .max(MAX_DEPARTURES_PER_PAGE)
      .default(DEPARTURES_PAGE_SIZE),
  })
  .strip();
export type DeparturesQuery = z.infer<typeof departuresQuerySchema>;

export const departureRowDtoSchema = z.object({
  /** Stable across requests: tour, date and start time. */
  id: z.string(),
  tourId: z.string().uuid(),
  date: isoDate,
  /** `HH:MM` when the tour runs at set times. */
  startTime: z.string().optional(),
  /** Last day of the trip: the start date for a tour measured in hours,
   *  `null` when the tour has no length set. */
  endDate: isoDate.nullable(),
  priceFrom: moneyDtoSchema,
  /** The price before a reduction, struck through beside `priceFrom`: the
   *  was-price of the price `priceFrom` came from. Absent ⇒ no reduction. */
  originalPrice: moneyDtoSchema.optional(),
  /** `null` is a tour with no seat limit. */
  spotsLeft: z.number().int().nullable(),
  status: z.enum(DEPARTURE_SEAT_STATUSES),
  /** "Guaranteed to run" (AC-21): every package still open on this date has
   *  met its minimum party, or sets none — `isGuaranteedToRun`, the tour
   *  page's rule. A folded row claims it only when it holds for whichever
   *  package the traveler picks. */
  guaranteed: z.boolean(),
});
export type DepartureRowDto = z.infer<typeof departureRowDtoSchema>;

export const departureTourDtoSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  durationUnit: z.enum(DURATION_UNITS).nullable(),
  durationValue: z.number().int().nonnegative().nullable(),
  durationLabel: z.string().optional(),
  /** Largest group one of this tour's departures in the answer takes, the
   *  first line of "See more details" (AC-19); `null` when there is no seat
   *  limit or none of its departures was worked out for this answer. */
  groupSize: z.number().int().positive().nullable(),
  /** "Instant confirmation" (AC-21): the tour is not priced on request, the
   *  tour page's rule. Per tour, not per date. */
  instantConfirmation: z.boolean(),
});
export type DepartureTourDto = z.infer<typeof departureTourDtoSchema>;

/**
 * The answer, as one envelope's `data` (`single()`): the page of rows and
 * everything the block's controls need. Not the paginated list envelope,
 * whose `meta` has no room for the rest.
 *
 * `total` is the count after every filter and after the block's `max`, so
 * Show more stops where AC-06 says. `year` and `month` are the view actually
 * shown — a value the address asked for that has no departures falls back
 * (AC-15), and the block reads these to show the controls in step. `years`
 * and `months` are what the year toggle and the month tabs offer (AC-04):
 * years with departures for the chosen tours, and months with departures in
 * `year`. `monthCounts` holds how many departures each of `months` has, in
 * the same order, before the block's `max` (the month strip shows them).
 * `tours` is every tour the block covers, whether or not it has a row on
 * this page; the tour picker is built from it.
 */
export const departuresPageSchema = z.object({
  items: z.array(departureRowDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  perPage: z.number().int().min(1),
  year: z.number().int().nullable(),
  month: z.number().int().min(1).max(12).nullable(),
  years: z.array(z.number().int()),
  months: z.array(z.number().int().min(1).max(12)),
  monthCounts: z.array(z.number().int().nonnegative()),
  tours: z.array(departureTourDtoSchema),
});
export type DeparturesPageDto = z.infer<typeof departuresPageSchema>;
